import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Currency = 'CZK' | 'EUR';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (curr: Currency) => void;
  formatPrice: (amount: number) => string;
  getCreditPricing: () => CreditPackage[];
}

interface CreditPackage {
  credits: number;
  price: number;
  originalPrice: number;
  savings: string;
  tag: string;
  discount: string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>('CZK');

  useEffect(() => {
    // Load currency from localStorage on mount
    const savedCurrency = localStorage.getItem('currency') as Currency;
    if (savedCurrency && (savedCurrency === 'CZK' || savedCurrency === 'EUR')) {
      setCurrencyState(savedCurrency);
    }
  }, []);

  const setCurrency = (curr: Currency) => {
    setCurrencyState(curr);
    localStorage.setItem('currency', curr);
  };

  const formatPrice = (amount: number): string => {
    if (currency === 'EUR') {
      return `€${Number(amount).toFixed(2)}`;
    }
    return `${amount} Kč`;
  };

  const getCreditPricing = (): CreditPackage[] => {
    if (currency === 'EUR') {
      return [
        {
          credits: 100,
          price: 4.00,
          originalPrice: 4.00,
          savings: 'No discount',
          tag: '💼',
          discount: ''
        },
        {
          credits: 250,
          price: 9.00,
          originalPrice: 10.00,
          savings: '10% savings',
          tag: '⭐️',
          discount: 'Popular -10%'
        },
        {
          credits: 500,
          price: 16.00,
          originalPrice: 20.00,
          savings: '20% savings',
          tag: '🔥',
          discount: '-20%'
        },
        {
          credits: 1000,
          price: 28.00,
          originalPrice: 40.00,
          savings: '30% savings',
          tag: '🏆',
          discount: 'Best Value -30%'
        }
      ];
    } else {
      // CZK pricing (1 credit = 1 CZK)
      return [
        {
          credits: 100,
          price: 100,
          originalPrice: 100,
          savings: 'No discount',
          tag: '💼',
          discount: ''
        },
        {
          credits: 250,
          price: 225,
          originalPrice: 250,
          savings: '10% savings',
          tag: '⭐️',
          discount: 'Popular -10%'
        },
        {
          credits: 500,
          price: 400,
          originalPrice: 500,
          savings: '20% savings',
          tag: '🔥',
          discount: '-20%'
        },
        {
          credits: 1000,
          price: 700,
          originalPrice: 1000,
          savings: '30% savings',
          tag: '🏆',
          discount: 'Best Value -30%'
        }
      ];
    }
  };

  const value: CurrencyContextType = {
    currency,
    setCurrency,
    formatPrice,
    getCreditPricing
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}; 